<script lang="ts">
  import { icons } from '../icons/index';
  import { t } from '../i18n/index';
  import { createAIService } from '../services/ai-service';
  import type { A11yPlusConfig, AIService, FeatureModule } from '../types';

  type ContrastMode = 'off' | 'light' | 'dark' | 'high';

  interface FeatureDef {
    id: string;
    nameKey: string;
    descKey: string;
    iconId: string;
  }

  let {
    config,
    activeFeatures,
    ontoggle,
    ontextsize,
  }: {
    config: A11yPlusConfig;
    activeFeatures: Map<string, boolean>;
    ontoggle: (id: string, enabled: boolean) => void;
    ontextsize?: (size: number) => void;
  } = $props();

  const FEATURE_REGISTRY: Record<string, FeatureDef> = {
    'keyboard-nav': { id: 'keyboard-nav', nameKey: 'feature.keyboardNav', descKey: 'feature.keyboardNav.desc', iconId: 'keyboard' },

    'dyslexia-font': { id: 'dyslexia-font', nameKey: 'feature.dyslexiaFont', descKey: 'feature.dyslexiaFont.desc', iconId: 'dyslexia-font' },
    'link-highlight': { id: 'link-highlight', nameKey: 'feature.linkHighlight', descKey: 'feature.linkHighlight.desc', iconId: 'link-highlight' },
    'reading-guide': { id: 'reading-guide', nameKey: 'feature.readingGuide', descKey: 'feature.readingGuide.desc', iconId: 'reading-guide' },
    'reading-mask': { id: 'reading-mask', nameKey: 'feature.readingMask', descKey: 'feature.readingMask.desc', iconId: 'reading-mask' },
    'animation-stop': { id: 'animation-stop', nameKey: 'feature.animationStop', descKey: 'feature.animationStop.desc', iconId: 'animation-pause' },
    'hide-images': { id: 'hide-images', nameKey: 'feature.hideImages', descKey: 'feature.hideImages.desc', iconId: 'hide-images' },
    'big-cursor': { id: 'big-cursor', nameKey: 'feature.bigCursor', descKey: 'feature.bigCursor.desc', iconId: 'cursor' },
    'page-structure': { id: 'page-structure', nameKey: 'feature.pageStructure', descKey: 'feature.pageStructure.desc', iconId: 'page-structure' },
    tts: { id: 'tts', nameKey: 'feature.tts', descKey: 'feature.tts.desc', iconId: 'tts' },
    'text-simplify': { id: 'text-simplify', nameKey: 'feature.textSimplify', descKey: 'feature.textSimplify.desc', iconId: 'simplify-text' },
    'alt-text': { id: 'alt-text', nameKey: 'feature.altText', descKey: 'feature.altText.desc', iconId: 'alt-text' },

    spacing: { id: 'spacing', nameKey: 'feature.spacing', descKey: 'feature.spacing.desc', iconId: 'spacing' },
    saturation: { id: 'saturation', nameKey: 'feature.saturation', descKey: 'feature.saturation.desc', iconId: 'saturation' },
    magnifier: { id: 'magnifier', nameKey: 'feature.magnifier', descKey: 'feature.magnifier.desc', iconId: 'magnifier' },
    'mute-media': { id: 'mute-media', nameKey: 'feature.muteMedia', descKey: 'feature.muteMedia.desc', iconId: 'mute' },
    'page-zoom': { id: 'page-zoom', nameKey: 'feature.pageZoom', descKey: 'feature.pageZoom.desc', iconId: 'page-zoom' },
    'cursor-highlight': { id: 'cursor-highlight', nameKey: 'feature.cursorHighlight', descKey: 'feature.cursorHighlight.desc', iconId: 'cursor-highlight' },
  };

  // Inline controls rendered separately (not as cards)
  const INLINE_CONTROLS = new Set(['contrast', 'text-size']);

  // Funktionen, die nur am Desktop Sinn ergeben: „Tastaturnavigation" braucht
  // ein physisches Keyboard, „Großer Mauszeiger" einen Maus-Cursor. Auf reinen
  // Touch-Geräten (Handy/Tablet) werden sie ausgeblendet UND nie aktiviert.
  const DESKTOP_ONLY_FEATURES = new Set(['keyboard-nav', 'big-cursor']);

  // „Hoher Kontrast" kennt genau zwei ehrliche Stufen: dieselbe verschärfte
  // Lesbarkeit, einmal auf hellem (Schwarz auf Weiß) und einmal auf dunklem
  // Grund (Weiß auf Schwarz). Das ist eine Polaritäts-Wahl, kein getarnter
  // Dark-Mode. Der `high`-Chip trägt bewusst das `contrast.mode.dark`-Label
  // („Dunkel"), weil der `high`-Token die dunkle Polarität ist; der frühere
  // weiche `dark`-Theme-Modus wird nicht mehr als Chip angeboten.
  const CONTRAST_MODES: Array<{ id: ContrastMode; labelKey: string; icon: string }> = [
    { id: 'light', labelKey: 'contrast.mode.light', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>' },
    { id: 'high', labelKey: 'contrast.mode.dark', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>' },
  ];

  const TEXT_SIZE_MIN = 75;
  const TEXT_SIZE_MAX = 200;
  const TEXT_SIZE_STEP = 5;
  const AI_FEATURES = new Set(['text-simplify', 'alt-text']);
  const loadedModules = new Map<string, FeatureModule>();
  const FEATURE_LOADERS: Record<string, () => Promise<any>> = {
    contrast: () => import('../features/contrast.ts'),
    'text-size': () => import('../features/text-size.ts'),
    'keyboard-nav': () => import('../features/keyboard-nav.ts'),

    'dyslexia-font': () => import('../features/dyslexia-font.ts'),
    'link-highlight': () => import('../features/link-highlight.ts'),
    'reading-guide': () => import('../features/reading-guide.ts'),
    'reading-mask': () => import('../features/reading-mask.ts'),
    'animation-stop': () => import('../features/animation-stop.ts'),
    'hide-images': () => import('../features/hide-images.ts'),
    'big-cursor': () => import('../features/big-cursor.ts'),
    'page-structure': () => import('../features/page-structure.ts'),
    tts: () => import('../features/tts.ts'),
    'text-simplify': () => import('../features/text-simplify.ts'),
    'alt-text': () => import('../features/alt-text.ts'),

    spacing: () => import('../features/spacing.ts'),
    saturation: () => import('../features/saturation.ts'),
    magnifier: () => import('../features/magnifier.ts'),
    'mute-media': () => import('../features/mute-media.ts'),
    'page-zoom': () => import('../features/page-zoom.ts'),
    'cursor-highlight': () => import('../features/cursor-highlight.ts'),
  };

  let contrastMode = $state<ContrastMode>(readStoredContrastMode());
  let textSize = $state(readStoredTextSize());

  // Erkennungsmechanismus „Touch-Gerät": grober Zeiger UND kein Hover = Handy/
  // Tablet (kein Maus-/Tastatur-Primärinput). Bewusst capability-basiert statt
  // per Viewport-Breite — ein schmales Desktop-Fenster behält Keyboard + Maus
  // und damit auch die Desktop-only-Funktionen. Reaktiv: ändert sich der
  // Eingabemodus, verschwinden/erscheinen die betroffenen Karten live.
  const touchQuery = typeof window !== 'undefined'
    ? window.matchMedia('(pointer: coarse) and (hover: none)')
    : null;
  let isTouchDevice = $state(touchQuery?.matches ?? false);
  $effect(() => {
    if (!touchQuery) return;
    const onChange = (e: MediaQueryListEvent) => { isTouchDevice = e.matches; };
    touchQuery.addEventListener('change', onChange);
    return () => touchQuery.removeEventListener('change', onChange);
  });

  // Click-popover for the info icon (one open at a time)
  let openInfoId = $state<string | null>(null);
  // Öffnungs-Richtung des Info-Popovers. Unten im (mobilen) Panel ist unter der
  // letzten Karten-Reihe kein Platz: das Popover liegt absolut, vergrößert also
  // die scrollHeight NICHT und lässt sich nicht „hereinscrollen" — es würde
  // hinter dem Panel-Rand abgeschnitten. Darum klappt es bei Platzmangel NACH
  // OBEN auf (über die Karte). Default unten.
  let openInfoDir = $state<'down' | 'up'>('down');

  function toggleInfo(id: string, btn?: HTMLElement): void {
    const willOpen = openInfoId !== id;
    if (!willOpen) { openInfoId = null; return; }
    // Richtung SYNCHRON bestimmen — VOR dem Setzen von openInfoId, damit das
    // Popover gleich in der richtigen Richtung rendert (kein Springen). Bewusst
    // KEIN requestAnimationFrame: das wird in Hintergrund-Tabs gedrosselt (im
    // Headless-Preview feuerte es nie). Die Karte ist bereits gerendert, also
    // reichen ihre aktuelle Position + eine konservative Popover-Höhe.
    //
    // Logik: passt das Popover unter die Karte, ohne in die unten verdeckte
    // Zone zu laufen (Body-Unterkante MINUS Safe-Area-Polster = `padding-bottom`,
    // sonst verschwindet es am Handy hinter Home-Indicator/Safari-Leiste)? Sonst
    // klappt es nach OBEN über die — ohnehin sichtbare — Karte.
    let dir: 'down' | 'up' = 'down';
    const wrap = btn?.closest('.accessify-card-wrap') as HTMLElement | null;
    const scroller = btn?.closest('.accessify-body') as HTMLElement | null;
    if (wrap && scroller) {
      const padB = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
      const safeBottom = scroller.getBoundingClientRect().bottom - padB;
      const roomBelow = safeBottom - wrap.getBoundingClientRect().bottom;
      dir = roomBelow >= 110 ? 'down' : 'up'; // ~110px = Beschreibung + Padding
    }
    openInfoDir = dir;
    openInfoId = id;
  }

  function closeInfo(): void {
    openInfoId = null;
  }

  // ESC + outside-click close the open popover. Both listeners only attach
  // while a popover is open and detach on close — no global listener leak.
  // composedPath() is used for the outside-click handler because the widget
  // lives inside a shadow root: e.target on a document-level listener would
  // otherwise point at the shadow host, never the inner element.
  $effect(() => {
    if (openInfoId === null) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') openInfoId = null;
    };
    const onPointer = (e: Event) => {
      const path = e.composedPath();
      for (const node of path) {
        if (!(node instanceof HTMLElement)) continue;
        const cls = node.classList;
        if (!cls) continue;
        if (cls.contains('accessify-card-info')) return;
        if (cls.contains('accessify-info-popover')) return;
      }
      openInfoId = null;
    };

    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  });

  let visibleFeatureIds = $derived(new Set(config.features));
  let activeSignature = $derived(
    Array.from(activeFeatures.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([id, enabled]) => `${id}:${enabled}`)
      .join('|')
  );

  let aiService: AIService | undefined;

  function readStoredContrastMode(): ContrastMode {
    if (typeof window === 'undefined') return 'off';
    const stored = localStorage.getItem('accessify-contrast-mode');
    return stored === 'light' || stored === 'dark' || stored === 'high' ? stored : 'off';
  }

  function readStoredTextSize(): number {
    if (typeof window === 'undefined') return 100;
    const stored = Number.parseInt(localStorage.getItem('accessify-text-size') || '100', 10);
    return Number.isFinite(stored) ? Math.max(100, Math.min(200, stored)) : 100;
  }

  function getAIService(): AIService {
    if (!aiService) {
      aiService = createAIService({
        apiKey: config.openRouterKey,
        siteKey: (config as any).siteKey,
        proxyUrl: (config as any).proxyUrl,
      });
    }
    return aiService;
  }

  function getIcon(iconId: string): string {
    const iconFn = icons[iconId];
    return iconFn ? iconFn() : '';
  }

  function getVisibleFeatures(ids: string[]): FeatureDef[] {
    return ids
      .filter((id) => visibleFeatureIds.has(id))
      .map((id) => FEATURE_REGISTRY[id])
      .filter(Boolean);
  }

  function updateControlState(id: string, module: FeatureModule | null | undefined): void {
    if (!module) return;
    const state = module.getState();
    if (id === 'contrast') {
      contrastMode =
        state.value === 'light' || state.value === 'dark' || state.value === 'high'
          ? state.value
          : 'off';
    }
    if (id === 'text-size') {
      textSize = typeof state.value === 'number' ? state.value : 100;
      ontextsize?.(textSize);
    }
  }

  function handleReset() {
    for (const [, module] of loadedModules) {
      try { module.deactivate(); } catch { /* ignore */ }
    }
    contrastMode = 'off';
    textSize = 100;
    ontextsize?.(100);
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('accessify:reset', handleReset);
  }

  async function loadFeatureModule(id: string): Promise<FeatureModule | null> {
    if (loadedModules.has(id)) return loadedModules.get(id)!;
    const loader = FEATURE_LOADERS[id];
    if (!loader) return null;
    try {
      const mod = await loader();
      const exported = mod.default ?? mod;
      const featureModule: FeatureModule =
        typeof exported === 'function'
          ? AI_FEATURES.has(id)
            ? exported(getAIService(), config.lang, { siteKey: (config as any).siteKey, proxyUrl: (config as any).proxyUrl, simplificationLevel: (config as any).simplificationLevel })
            : exported()
          : exported;
      loadedModules.set(id, featureModule);
      return featureModule;
    } catch (error) {
      console.warn(`[Accessify] Failed to load feature module "${id}":`, error);
      return null;
    }
  }

  async function syncActiveModules(_: string): Promise<void> {
    const idsToSync = new Set<string>(['contrast', 'text-size', ...config.features]);
    for (const id of idsToSync) {
      // Touch-Gerät: Desktop-only-Funktionen nie aktiv halten — auch nicht, wenn
      // sie aus einer früheren Desktop-Sitzung als aktiv gespeichert wurden. Sonst
      // liefe z. B. die Tastaturnavigation unsichtbar weiter, obwohl die Karte
      // (capability-basiert) ausgeblendet ist.
      if (isTouchDevice && DESKTOP_ONLY_FEATURES.has(id)) {
        const loaded = loadedModules.get(id);
        if (loaded && loaded.getState().enabled) { try { loaded.deactivate(); } catch { /* ignore */ } }
        continue;
      }
      const shouldBeEnabled = activeFeatures.get(id) ?? false;
      const alreadyLoaded = loadedModules.get(id);
      if (!shouldBeEnabled && !alreadyLoaded) {
        if (id === 'contrast') contrastMode = 'off';
        if (id === 'text-size') textSize = 100;
        continue;
      }
      const module = shouldBeEnabled ? await loadFeatureModule(id) : alreadyLoaded;
      if (!module) continue;
      const isEnabled = module.getState().enabled;
      if (shouldBeEnabled && !isEnabled) module.activate();
      if (!shouldBeEnabled && isEnabled) module.deactivate();
      updateControlState(id, module);
    }
  }

  async function handleToggle(id: string): Promise<void> {
    const isActive = activeFeatures.get(id) ?? false;
    const nextState = !isActive;
    const module = await loadFeatureModule(id);
    if (module) {
      if (nextState) module.activate();
      else module.deactivate();
      updateControlState(id, module);
    }
    ontoggle(id, nextState);
  }

  async function setContrastMode(mode: ContrastMode): Promise<void> {
    const module = await loadFeatureModule('contrast');
    if (!module) return;
    if (mode === 'off') {
      module.deactivate();
      contrastMode = 'off';
      ontoggle('contrast', false);
    } else {
      if (!module.getState().enabled) module.activate();
      module.setState?.(mode);
      contrastMode = mode;
      ontoggle('contrast', true);
    }
    // Page → widget: tell the shell to match the page scheme (Hoher Kontrast →
    // dark widget; off → restore the widget's own appearance). The widget's
    // own dark-mode never feeds back into the page.
    window.dispatchEvent(new CustomEvent('accessify:contrast-changed', { detail: { mode } }));
  }

  async function setTextSize(value: number): Promise<void> {
    const module = await loadFeatureModule('text-size');
    if (!module) return;
    if (value === 100) {
      module.deactivate();
      textSize = 100;
      ontoggle('text-size', false);
      ontextsize?.(100);
      return;
    }
    if (!module.getState().enabled) module.activate();
    module.setState?.(value);
    textSize = value;
    ontoggle('text-size', true);
    ontextsize?.(value);
  }

  // Feature cards: ordered by config.features, excluding inline controls.
  let cardFeatures = $derived(
    config.features
      .filter((id: string) => !INLINE_CONTROLS.has(id) && FEATURE_REGISTRY[id])
      .filter((id: string) => !(isTouchDevice && DESKTOP_ONLY_FEATURES.has(id)))
      .map((id: string) => FEATURE_REGISTRY[id])
  );

  $effect(() => {
    const signature = `${config.features.join('|')}|${activeSignature}|${isTouchDevice}`;
    void syncActiveModules(signature);
  });
</script>

<!-- Contrast: inline radiogroup.
       Two high-contrast polarities (light / dark) plus "off" (no chip
       active), so a radiogroup with re-click-to-deselect is closer to the
       actual UX than aria-pressed toggles. -->
  {#if visibleFeatureIds.has('contrast')}
    <div class="accessify-control-row">
      <div class="accessify-control-label">
        <span class="accessify-control-label-icon" aria-hidden="true">
          {@html getIcon('contrast')}
        </span>
        <span class="accessify-control-label-text" id="accessify-contrast-label">{t('feature.contrast', config.lang)}</span>
      </div>
      <div class="accessify-chip-row accessify-chip-row--two" role="radiogroup" aria-labelledby="accessify-contrast-label" data-feature="contrast">
        {#each CONTRAST_MODES as mode}
          <button
            type="button"
            class="accessify-chip accessify-chip--icon"
            role="radio"
            aria-checked={contrastMode === mode.id}
            data-active={contrastMode === mode.id}
            data-feature="contrast"
            data-mode={mode.id}
            onclick={() => setContrastMode(contrastMode === mode.id ? 'off' : mode.id)}
          >
            <span class="accessify-chip-icon" aria-hidden="true">{@html mode.icon}</span>
            <span>{t(mode.labelKey, config.lang)}</span>
          </button>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Text Size: 3-step radiogroup.
       Semantic note: the three chips are a one-of-three choice, not three
       independent toggles. WAI-ARIA's `radiogroup` + `radio` pattern is the
       right primitive — screen readers announce "1 of 3 selected" instead
       of "button, pressed" three times, and the `aria-label` on each radio
       gives the localized full label (visual text is just "A Kleiner" etc.,
       which is too cryptic on its own). -->
  {#if visibleFeatureIds.has('text-size')}
    <div class="accessify-control-row">
      <div class="accessify-control-label">
        <span class="accessify-control-label-icon" aria-hidden="true">
          {@html getIcon('text-size')}
        </span>
        <span class="accessify-control-label-text" id="accessify-text-size-label">{t('feature.textSize', config.lang)}</span>
      </div>
      <div class="accessify-chip-row" role="radiogroup" aria-labelledby="accessify-text-size-label" data-feature="text-size">
        {#each [
          { pct: 85,  size: '12px', label: config.lang?.startsWith('de') ? 'Kleiner'   : 'Smaller',
            aria: config.lang?.startsWith('de') ? 'Schriftgröße kleiner als Standard' : 'Smaller than default text size' },
          { pct: 130, size: '20px', label: config.lang?.startsWith('de') ? 'Groß'      : 'Large',
            aria: config.lang?.startsWith('de') ? 'Schriftgröße groß' : 'Large text size' },
          { pct: 160, size: '26px', label: config.lang?.startsWith('de') ? 'Sehr groß' : 'Very large',
            aria: config.lang?.startsWith('de') ? 'Schriftgröße sehr groß' : 'Very large text size' },
        ] as step}
          <button
            type="button"
            class="accessify-chip accessify-chip--icon"
            role="radio"
            aria-checked={textSize === step.pct}
            aria-label={step.aria}
            data-active={textSize === step.pct}
            data-feature="text-size"
            data-size={step.pct}
            onclick={() => setTextSize(textSize === step.pct ? 100 : step.pct)}
          >
            <span style="font-size:{step.size};font-weight:700;line-height:1" aria-hidden="true">A</span>
            <span aria-hidden="true">{step.label}</span>
          </button>
        {/each}
      </div>
    </div>
  {/if}

  <div class="accessify-section-divider"></div>

  <!-- Single flat grid, ordered by config.features. No sub-sections,
       no category headings — Marvin's product decision: one continuous
       row-flow of feature cards, sighted + AT users see the same shape. -->
  {#if cardFeatures.length > 0}
    <div class="accessify-features">
      {#each cardFeatures as feature (feature.id)}
        {@const isActive = activeFeatures.get(feature.id) ?? false}
        <div class="accessify-card-wrap" data-feature={feature.id}>
          <button
            class="accessify-card"
            data-active={isActive}
            data-feature={feature.id}
            role="switch"
            aria-checked={isActive}
            aria-label={t(feature.nameKey, config.lang)}
            onclick={() => handleToggle(feature.id)}
          >
            <span class="accessify-card-icon" aria-hidden="true">
              {@html getIcon(feature.iconId)}
            </span>
            <span class="accessify-card-label">{t(feature.nameKey, config.lang)}</span>
          </button>
          <button
            type="button"
            class="accessify-card-info"
            aria-label={t(feature.descKey, config.lang)}
            aria-expanded={openInfoId === feature.id}
            onclick={(e) => { e.stopPropagation(); toggleInfo(feature.id, e.target as HTMLElement); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          </button>
          {#if openInfoId === feature.id}
            <div class="accessify-info-popover" data-dir={openInfoDir} role="tooltip">
              <span class="accessify-info-popover-arrow" aria-hidden="true"></span>
              <span class="accessify-info-popover-text">{t(feature.descKey, config.lang)}</span>
              <button
                type="button"
                class="accessify-info-popover-close"
                aria-label={config.lang?.startsWith('de') ? 'Schließen' : 'Close'}
                onclick={(e) => { e.stopPropagation(); closeInfo(); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
